import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../../assets/logo-dark.webp";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "../../constants/LandingUtils";
import CloseIcon from "@mui/icons-material/Close";
import AppPreview from "../../assets/landing/app-preview.webp";

const navItems = [
    { label: "Home", sectionId: "home" },
    { label: "About", sectionId: "about" },
    { label: "Services", sectionId: "services" },
    { label: "Testimonials", sectionId: "testimonials" },
    { label: "Contact Us", sectionId: "contact" },
];

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width:900px)");

    const [open, setOpen] = useState(false);
    const [activeSection, setActive] = useState("home");
    const [scrolled, setScrolled] = useState(false);
    const [inServices, setInServices] = useState(false);

    // ── One ref per nav item to measure position ─────────────────
    const navRefs = useRef([]);
    const pillRef = useRef(null);

    const isHomePage = location.pathname === "/";

    // ── Move the pill to the active item ─────────────────────────
    useEffect(() => {
        const activeIndex = navItems.findIndex(
            (item) => item.sectionId === activeSection
        );
        const activeEl = navRefs.current[activeIndex];
        const pill = pillRef.current;

        if (!activeEl || !pill) return;

        const { offsetLeft, offsetWidth } = activeEl;

        pill.style.transform = `translateX(${offsetLeft}px)`;
        pill.style.width = `${offsetWidth}px`;

    }, [activeSection]);

    // ── Scroll listener ──────────────────────────────────────────
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 25);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // ── IntersectionObserver for active section ──────────────────
    useEffect(() => {
        if (!isHomePage) return;

        const sectionIds = navItems.map((item) => item.sectionId);

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActive(entry.target.id);
                        setInServices(entry.target.id === "services");
                    }
                });
            },
            { threshold: 0.3, rootMargin: "-80px 0px 0px 0px" }
        );

        sectionIds.forEach((id) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [isHomePage]);

    const handleNavClick = (sectionId) => {
        setActive(sectionId);
        if (!isHomePage) {
            navigate("/");
            setTimeout(() => scrollToSection(sectionId), 300);
            return;
        }
        scrollToSection(sectionId);
    };

    return (
        <>
            <div
                style={{
                    background: scrolled ? "rgb(255 255 255 / 20%)" : "rgb(245 247 255 / 15%)",
                    padding: isMobile ? "10px 15px" : "10px 40px",
                    maxWidth: !isMobile && scrolled ? "fit-content" : "1200px",
                    borderRadius: scrolled ? "50px" : "16px",
                    margin: "0 auto",
                    backdropFilter: "blur(8px)",
                    boxShadow: "inset -3px 3px 5px #979eff63, inset 1px -1px 5px 0px #00000012",
                    transition: "max-width 0.6s ease, border-radius 0.6s ease, background 0.8s ease",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "50px" }}>

                    {/* Logo */}
                    <img
                        src={Logo}
                        alt="Logo"
                        style={{
                            width: "100%",
                            maxWidth: "160px",
                            cursor: "pointer",
                            opacity: !isMobile && scrolled ? 0 : 1,
                            visibility: !isMobile && scrolled ? "hidden" : "visible",
                            transition: "opacity 0.5s ease, visibility 0.5s ease",
                            // ✅ keep width so navbar doesn't jump/reflow
                            maxWidth: !isMobile && scrolled ? "0px" : "160px",
                            overflow: "hidden",
                            transition: "opacity 0.5s ease, visibility 0.5s ease, max-width 0.5s ease",
                        }}
                        onClick={() => handleNavClick("home")}
                    />

                    {/* Desktop nav */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                position: "relative",  // 👈 needed for pill absolute positioning
                            }}
                        >
                            {/* ── Sliding pill background ───────────────── */}
                            <Box
                                ref={pillRef}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    height: "100%",
                                    borderRadius: "40px",
                                    background: "linear-gradient(180deg, #c4c8ff45 30%, #868df563 100%)",
                                    border: "1px solid #C4C8FF",
                                    borderBottom: "2px solid #7b7de5ab",
                                    boxShadow: "inset 0px -2px 1px #979EFF66, inset 0px 2px 1px #ffffff66, 0px 2px 15px #979EFF66",
                                    // ── This is the smooth slide ──────────
                                    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                    pointerEvents: "none",  // 👈 pill is visual only, clicks pass through
                                    zIndex: 0,
                                }}
                            />

                            {/* ── Nav items ─────────────────────────────── */}
                            {navItems.map((item, index) => (
                                <Typography
                                    key={item.label}
                                    ref={(el) => (navRefs.current[index] = el)}  // 👈 store ref
                                    component="span"
                                    onClick={() => handleNavClick(item.sectionId)}
                                    sx={{
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        padding: "2px 10px",
                                        fontWeight: 500,
                                        position: "relative", // 👈 sits above the pill
                                        zIndex: 1,
                                        // no active border/bg styles here — pill handles it
                                        color: isHomePage && activeSection === item.sectionId
                                            ? "#5e60e4" : scrolled
                                                ? "rgb(79, 81, 203)"
                                                : "#7B7DE5",
                                        transition: "color 0.3s ease",
                                        "&:hover": { color: "#5e60e4" },
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* Login button / hamburger */}
                    {!isMobile ? (
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/login")}
                            sx={{
                                borderRadius: "40px",
                                color: "#5e60e4",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                background: "linear-gradient(180deg, #c4c8ff45 30%, #868df563 100%)",
                                border: "solid 1px #C4C8FF",
                                borderBottom: "2px solid #7b7de5ab",
                                boxShadow: "inset 0px -2px 1px #979EFF66, inset 0px 2px 1px #ffffff66",
                                "&:hover": { boxShadow: "0px 2px 15px #979EFF66" },
                                // ✅ smooth fade instead of display none
                                padding: scrolled ? "0px" : "5px 12px",
                                opacity: scrolled ? 0 : 1,
                                visibility: scrolled ? "hidden" : "visible",
                                maxWidth: scrolled ? "0px" : "200px",
                                minWidth: scrolled ? "0px" : "fit-content",
                                overflow: "hidden",
                                transition: "opacity 0.5s ease, visibility 0.5s ease, max-width 0.5s ease",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Login / Sign Up
                        </Button>
                    ) : (
                        <IconButton
                            sx={{
                                padding: '5px',
                                background: "linear-gradient(180deg, #c4c8ff45 30%, #868df563 100%)",
                                border: "solid 1px #C4C8FF",
                                borderRadius: "8px",
                                borderBottom: "2px solid #7b7de5ab",
                                boxShadow: "inset 0px -2px 1px #979EFF66, inset 0px 2px 1px #ffffff66",
                            }}
                            onClick={() => setOpen(true)}>
                            <MenuIcon sx={{ color: "#7B7DE5" }} />
                        </IconButton>
                    )}
                </div>
            </div>

            {/* Mobile Drawer */}
            {/* <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
                <Box sx={{ width: 250 }}>
                    <List>
                        {navItems.map((item) => (
                            <ListItem
                                button
                                key={item.label}
                                onClick={() => {
                                    setOpen(false);
                                    handleNavClick(item.sectionId);
                                }}
                                sx={{
                                    color: activeSection === item.sectionId ? "#5e60e4" : "#7B7DE5",
                                    background: activeSection === item.sectionId
                                        ? "linear-gradient(180deg, #c4c8ff45 30%, #868df563 100%)"
                                        : "transparent",
                                }}
                            >
                                <ListItemText primary={item.label} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer> */}


            {/* Mobile Drawer — full screen */}
            <Drawer
                anchor="right"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        width: "100%",
                        height: "100%",
                        background: "#F2F3FD",
                        p: 0,
                    }
                }}
            >

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, px: 2, pt: 2 }}>
                    <img src={Logo} alt="Logo" style={{ maxWidth: "130px" }} />
                    <IconButton onClick={() => setOpen(false)}>
                        <CloseIcon sx={{ color: "#4F51CB" }} />
                    </IconButton>
                </Box>
                <Box sx={{ display: "flex", gap: 2, height: "100%" }}>

                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>

                        {/* Nav links */}
                        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, pl: 2 }}>
                            {navItems.map((item) => {
                                const isActive = activeSection === item.sectionId;
                                return (
                                    <Box
                                        key={item.label}
                                        onClick={() => {
                                            setOpen(false);
                                            handleNavClick(item.sectionId);
                                        }}
                                        sx={{
                                            px: 1.8,
                                            py: 0.9,
                                            borderRadius: "50px",
                                            cursor: "pointer",
                                            background: isActive
                                                ? "linear-gradient(180deg, #c4c8ff26 30%, #868df545 100%)"
                                                : "transparent",
                                            border: isActive ? "1px solid #C4C8FF" : "1px solid transparent",
                                            boxShadow: isActive
                                                ? "inset 0px -2px 1px #979EFF66, inset 0px 2px 1px #ffffff66"
                                                : "none",
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 14, sm: 15 },
                                                fontWeight: 400,
                                                color: isActive ? "#4F51CB" : "#7B7DE5",
                                                transition: "color 0.3s ease",
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Box>
                                );
                            })}

                            {/* Login button at bottom */}
                            <Button
                                variant="contained"
                                onClick={() => { setOpen(false); navigate("/login"); }}
                                sx={{
                                    mt: 3,
                                    borderRadius: "50px",
                                    textTransform: "none",
                                    fontSize: 15,
                                    fontWeight: 600,
                                    py: 1,
                                    px: 1,
                                    background: "linear-gradient(180deg, #c4c8ff70 30%, #868df580 100%)",
                                    border: "1px solid #C4C8FF",
                                    borderBottom: "2px solid #7b7de5ab",
                                    color: "#4F51CB",
                                    boxShadow: "inset 0px -2px 1px #979EFF66, inset 0px 2px 1px #ffffff66",
                                    "&:hover": {
                                        boxShadow: "0px 2px 15px #979EFF66",
                                        background: "linear-gradient(180deg, #c4c8ff90 30%, #868df5a0 100%)",
                                    },
                                }}
                            >
                                Login / Sign Up
                            </Button>
                        </Box>


                    </Box>

                    {/* RIGHT SIDE — preview image */}
                    <Box
                        sx={{
                            width: { xs: "55%", sm: "60%" },
                            borderRadius: "24px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src={AppPreview}   // 👈 replace with your actual preview image import
                            alt="app preview"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                objectPosition: "top",
                            }}
                        />
                    </Box>

                </Box>
            </Drawer>
        </>
    );
};

export default Navbar;